import { useEffect, useState } from 'react';
import { impersonation, type ImpersonationState } from '../auth/impersonation';
import { stopImpersonation } from '../api';
import { dataColors } from '../shared/design/dataColors';

/**
 * Persistent bright-red strip rendered above every admin page while
 * impersonation is active. Shows target + remaining time. Stop button
 * calls the IdP, clears local state, reloads.
 *
 * Bright red on purpose — every screenshot, every demo, every accidental
 * Slack share must show IMMEDIATELY that the session is impersonated.
 * Operators have shipped destructive things by forgetting they were
 * impersonating someone else.
 *
 * Listens for the `cb:impersonation:expired` event (fired by the http
 * interceptor on 401 during impersonation) so a server-side expiry
 * triggers a banner refresh + reload without polling lag.
 */
export default function ImpersonationBanner() {
  const [state, setState] = useState<ImpersonationState | null>(impersonation.get());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    impersonation.bootstrap();
    setState(impersonation.get());
    const unsub = impersonation.subscribe(setState);
    const onExpired = () => {
      setState(null);
      window.location.reload();
    };
    window.addEventListener('cb:impersonation:expired', onExpired);
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      unsub();
      window.removeEventListener('cb:impersonation:expired', onExpired);
      window.clearInterval(id);
    };
  }, []);

  if (!state) return null;

  const msRemaining = new Date(state.expiresAt).getTime() - Date.now() - tick * 0;
  if (msRemaining <= 0) {
    // Natural expiry hit while page is open. Trigger the same flow as a
    // 401-driven expiry: clear + reload back to admin's normal session.
    impersonation.clear();
    setTimeout(() => window.location.reload(), 0);
    return null;
  }
  const min = Math.floor(msRemaining / 60_000);
  const sec = Math.floor((msRemaining % 60_000) / 1000);

  const onStop = async () => {
    const current = impersonation.get();
    if (!current) return;
    try {
      await stopImpersonation(current.accessToken);
    } catch {
      // Swallow — local clear + reload is still the right UX regardless
      // of whether the server registered the stop.
    }
    impersonation.clear();
    window.location.reload();
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: dataColors().alert.solid,
        color: 'white',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        fontSize: 14,
      }}
    >
      <div>
        <strong style={{ marginRight: 8 }}>🔴 IMPERSONATING:</strong>
        <span>{state.targetLabel}</span>
        <span style={{ marginLeft: 12, opacity: 0.85 }}>
          ({min}:{String(sec).padStart(2, '0')} left)
        </span>
      </div>
      <button
        type="button"
        onClick={onStop}
        style={{
          background: 'white',
          color: dataColors().alert.text,
          border: 'none',
          padding: '4px 12px',
          borderRadius: 4,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Stop impersonating
      </button>
    </div>
  );
}
